import {
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import type { ComponentProps } from "react";
import { Bar } from "react-chartjs-2";

import { abbreviatedNum } from "~/lib/abbreviated-num";
import {
  CHART_BACKGROUND_COLORS,
  CHART_BORDER_COLORS,
  TEXT_GRAY_500,
} from "~/lib/colors";

Chart.register([CategoryScale, LinearScale, BarElement, Tooltip, Legend]);

type Props = Readonly<{
  labels: string[];
  options?: ComponentProps<typeof Bar>["options"];
  values: Record<string, number[]>;
  barThickness?: number;
  datasetOptions?: {
    borderWidth: number;
    fill: boolean;
    borderRadius?: number;
  };
}>;

export function StackedBar({
  labels,
  options,
  values,
  barThickness,
  datasetOptions = {
    borderWidth: 1,
    fill: true,
  },
}: Props): JSX.Element {
  Chart.defaults.color = TEXT_GRAY_500;

  const datasets = Object.entries(values).map(([label, data], idx) => ({
    ...datasetOptions,
    backgroundColor: CHART_BACKGROUND_COLORS[idx],
    borderColor: CHART_BORDER_COLORS[idx],
    barThickness,
    data,
    label,
  }));

  return (
    <>
      <Bar
        data={{ datasets, labels }}
        options={{
          indexAxis: "x",
          plugins: {
            legend: { labels: { boxHeight: 5, usePointStyle: true } },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  let label = ctx.dataset.label ?? "";
                  if (label) label += ": ";
                  if (ctx.parsed.y != null) {
                    label += `$${abbreviatedNum(ctx.parsed.y)}`;
                  }
                  return label;
                },
              },
            },
          },
          responsive: true,
          scales: {
            x: { stacked: true, display: false },
            y: {
              stacked: true,
              grid: { color: "rgba(100, 100, 100, 0.40" },
              ticks: {
                callback: (v) =>
                  typeof v === "number" ? `$${abbreviatedNum(v)}` : v,
              },
            },
          },
          ...options,
        }}
      />
    </>
  );
}
